import { useState, useEffect } from 'react'
import { useToast } from "@/components/ui/use-toast"
import { Button } from "@/components/ui/button"
import { Plus, Pencil, Trash2, AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { supabase } from '@/lib/supabase'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { useRole } from '@/lib/useRole'

// Form validation schema
const formSchema = z.object({
    title: z.string().min(1, "Title is required"),
    message: z.string().min(1, "Message is required"),
    active: z.boolean(),
    priority: z.number().min(1).max(10),
    target_user_segment: z.string(),
    action_url: z.string()
        .refine(
            (val) => {
                if (!val) return true; // Allow empty string
                // Check if it's a valid URL
                try {
                    new URL(val);
                    return true;
                } catch {
                    // If not a valid URL, check if it's a valid relative path
                    // Allow paths that start with / or ./ or ../
                    return /^\/|\.\.?\//.test(val);
                }
            },
            "Must be a valid URL or relative path (starting with /, ./, or ../)"
        )
        .optional()
        .or(z.literal("")),
    action_text: z.string().optional(),
    start_date: z.string(),
    end_date: z.string().optional(),
})

type MarketingMessage = {
    id: string
    title: string
    message: string
    active: boolean
    priority: number
    target_user_segment: string
    action_url: string | null
    action_text: string | null
    start_date: string
    end_date: string | null
    created_at: string
    updated_at: string
}

export default function MarketingMessagesManagement() {
    const [messages, setMessages] = useState<MarketingMessage[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [editingMessage, setEditingMessage] = useState<MarketingMessage | null>(null)
    const { toast } = useToast()
    const { isSuperAdmin } = useRole()

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            message: "",
            active: true,
            priority: 1,
            target_user_segment: "all",
            action_url: "",
            action_text: "",
            start_date: new Date().toISOString().split('T')[0],
            end_date: "",
        },
    })

    useEffect(() => {
        console.log('[MarketingMessages] Component mounted, isSuperAdmin:', isSuperAdmin)
        if (isSuperAdmin) {
            fetchMessages()
        }
    }, [isSuperAdmin])

    useEffect(() => {
        console.log('[MarketingMessages] Messages state updated:', messages.length, 'messages')
    }, [messages])

    useEffect(() => {
        if (editingMessage) {
            form.reset({
                title: editingMessage.title,
                message: editingMessage.message,
                active: editingMessage.active,
                priority: editingMessage.priority,
                target_user_segment: editingMessage.target_user_segment,
                action_url: editingMessage.action_url || "",
                action_text: editingMessage.action_text || "",
                start_date: editingMessage.start_date.split('T')[0],
                end_date: editingMessage.end_date ? editingMessage.end_date.split('T')[0] : "",
            })
        }
    }, [editingMessage, form])

    const fetchMessages = async () => {
        console.log('[MarketingMessages] Fetching messages...')
        const { data, error } = await supabase
            .schema('marketing_and_customer_success')
            .from('marketing_messages')
            .select('*')
            .order('priority', { ascending: false })

        console.log('[MarketingMessages] Fetch result:', {
            dataLength: data?.length,
            data,
            error,
            hasError: !!error
        })

        if (error) {
            console.error('[MarketingMessages] Error fetching messages:', error)
            toast({
                title: "Error fetching messages",
                description: error.message,
                variant: "destructive",
            })
            return
        }

        console.log('[MarketingMessages] Setting messages state with', data?.length, 'messages')
        setMessages(data as MarketingMessage[])
    }

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        console.log('[MarketingMessages] onSubmit called with values:', values)
        const messageData = {
            ...values,
            action_url: values.action_url || null,
            action_text: values.action_text || null,
            end_date: values.end_date || null,
        }

        let error
        let data
        if (editingMessage) {
            console.log('[MarketingMessages] Updating message:', editingMessage.id)
            const result = await supabase
                .schema('marketing_and_customer_success')
                .from('marketing_messages')
                .update(messageData)
                .eq('id', editingMessage.id)
                .select()
            error = result.error
            data = result.data
        } else {
            console.log('[MarketingMessages] Inserting new message')
            const result = await supabase
                .schema('marketing_and_customer_success')
                .from('marketing_messages')
                .insert([messageData])
                .select()
            error = result.error
            data = result.data
        }

        console.log('[MarketingMessages] Save result:', { error, data, hasError: !!error, hasData: !!data })

        if (error) {
            console.error('[MarketingMessages] Error saving message:', error)
            toast({
                title: "Error saving message",
                description: error.message,
                variant: "destructive",
            })
            return
        }

        toast({
            title: "Success",
            description: `Marketing message ${editingMessage ? 'updated' : 'created'} successfully`,
        })

        setIsOpen(false)
        setEditingMessage(null)
        form.reset()

        console.log('[MarketingMessages] About to fetch messages after save')
        await fetchMessages()
        console.log('[MarketingMessages] Finished fetching messages after save')
    }

    const handleDelete = async (id: string) => {
        const { error } = await supabase
            .schema('marketing_and_customer_success')
            .from('marketing_messages')
            .delete()
            .eq('id', id)

        if (error) {
            toast({
                title: "Error deleting message",
                description: error.message,
                variant: "destructive",
            })
            return
        }

        toast({
            title: "Success",
            description: "Marketing message deleted successfully",
        })

        fetchMessages()
    }

    const handleEdit = (message: MarketingMessage) => {
        setEditingMessage(message)
        setIsOpen(true)
    }

    const handleAddNew = () => {
        setEditingMessage(null)
        form.reset()
        setIsOpen(true)
    }

    if (!isSuperAdmin) {
        return (
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                    You must be a superadmin to manage marketing messages.
                </AlertDescription>
            </Alert>
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Marketing Messages</h2>
                <Button onClick={handleAddNew}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Message
                </Button>
            </div>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>
                            {editingMessage ? 'Edit Marketing Message' : 'Create Marketing Message'}
                        </DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Title</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="message"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Message</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="active"
                                    render={({ field }) => (
                                        <FormItem className="flex items-center justify-between">
                                            <FormLabel>Active</FormLabel>
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="priority"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Priority (1-10)</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    max="10"
                                                    {...field}
                                                    onChange={(e) => field.onChange(parseInt(e.target.value))}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <FormField
                                control={form.control}
                                name="target_user_segment"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Target User Segment</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="e.g., all, premium, free" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="start_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Start Date</FormLabel>
                                            <FormControl>
                                                <Input type="date" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="end_date"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>End Date (Optional)</FormLabel>
                                            <FormControl>
                                                <Input type="date" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <FormField
                                control={form.control}
                                name="action_url"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Action URL (Optional)</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="https:// or /relative/path" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="action_text"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Action Text (Optional)</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="e.g., Learn More" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Button type="submit">
                                {editingMessage ? 'Update Message' : 'Create Message'}
                            </Button>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Active</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Target Segment</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead>Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {messages.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                No marketing messages found. Create your first message to get started.
                            </TableCell>
                        </TableRow>
                    ) : (
                        messages.map((message) => (
                            <TableRow key={message.id}>
                                <TableCell>{message.title}</TableCell>
                                <TableCell className="max-w-xs truncate">{message.message}</TableCell>
                                <TableCell>{message.active ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{message.priority}</TableCell>
                                <TableCell>{message.target_user_segment}</TableCell>
                                <TableCell>{new Date(message.start_date).toLocaleDateString()}</TableCell>
                                <TableCell>
                                    {message.end_date ? new Date(message.end_date).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell>
                                    <div className="flex space-x-2">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleEdit(message)}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleDelete(message.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    )
} 